/**
 * UpiPayButton — iter235.
 *
 * "Pay via UPI" deep-link button + post-return confirmation. UPI intent links
 * carry no success callback, so after the user comes back from GPay/PhonePe we
 * ask "did it go through?" — Yes records the settlement, No cancels.
 */
import { useState } from 'react';
import { Smartphone, Check, X } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { formatNXC } from '../lib/formatNXC';

export default function UpiPayButton({ upiId, payeeName, amount, note, onConfirmed }) {
  const [awaiting, setAwaiting] = useState(false);
  if (!upiId || !amount || amount <= 0) return null;

  const href = `upi://pay?pa=${encodeURIComponent(upiId)}`
    + `&pn=${encodeURIComponent(payeeName || 'Member')}`
    + `&am=${Math.round(amount)}`
    + `&cu=INR`
    + `&tn=${encodeURIComponent(note || 'SplitEasy settle')}`;

  if (awaiting) {
    return (
      <Card className="p-3 rounded-2xl border-2 border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2" data-testid="upi-pay-confirm-card">
        <p className="text-sm font-semibold text-center">
          Did the {formatNXC(amount)} payment go through in your UPI app?
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full text-xs"
            onClick={() => setAwaiting(false)}
            data-testid="upi-pay-no"
          >
            <X className="w-3.5 h-3.5 mr-1" /> No / cancel
          </Button>
          <Button
            className="flex-1 rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => { setAwaiting(false); onConfirmed?.(); }}
            data-testid="upi-pay-yes"
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Yes — record it
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          "Yes" books the settlement in the group ledger.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-1">
      <a
        href={href}
        onClick={() => setTimeout(() => setAwaiting(true), 400)}
        className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors"
        data-testid="upi-pay-btn"
      >
        <Smartphone className="w-4 h-4" />
        Pay {formatNXC(amount)} via UPI
      </a>
      <p className="text-xs text-muted-foreground text-center">
        Opens GPay / PhonePe / Paytm with {payeeName?.split(' ')[0] || 'payee'}'s UPI ({upiId}) pre-filled
      </p>
    </div>
  );
}
